import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RecordTrail from "@/components/RecordTrail";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Send, FileText, MessageSquare,
         CheckCircle2, AlertTriangle, FileWarning, XCircle, ShieldCheck,
         PlayCircle, ClipboardCheck, Pencil, Trash2, ShieldAlert } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { downloadAttachment, getSignedDownloadUrl } from "@/lib/downloadAttachment";

/**
 * P0 (2026-07-14) — inline image preview that survives the auth guard.
 * Plain `<img src="/api/attachments/…/download">` can't send the JWT
 * header, so we resolve a signed short-lived URL on mount and refresh
 * it every 55 s while the component stays visible.
 */
function InlineAttachmentImage({ att }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let timer = null;
    async function tick() {
      try {
        const url = await getSignedDownloadUrl(att.id);
        if (alive) setSrc(url);
      } catch { /* toast handled by helper */ }
      timer = setTimeout(tick, 55000);
    }
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [att.id]);
  if (!src) return null;
  return (
    <img src={src} alt={att.original_name}
         className="w-full max-h-[500px] object-contain rounded border border-slate-200 bg-slate-50" />
  );
}
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import OriginBadge from "@/components/OriginBadge";
import AttachmentDropzone from "@/components/AttachmentDropzone";
import {
  STATUS_LABELS, STATUS_STYLE, typeLabel, methodLabel, fmtUSD,
} from "@/lib/tx";

function fmt(d) {
  if (!d) return "—";
  return d.length > 10 ? new Date(d).toLocaleString() : d;
}

export default function TransactionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showAddAttach, setShowAddAttach] = useState(false);
  const [pendingAttach, setPendingAttach] = useState([]);
  // Post-modal state (Senior+ picks GL accounts at post time)
  const [postOpen, setPostOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [postExpense, setPostExpense] = useState("5900");
  const [postBank, setPostBank] = useState("1010");
  // Phase 9c Fix #8 — admin override delete dialog state (must be at top level)
  const [adminDelOpen, setAdminDelOpen] = useState(false);
  const [adminDelBusy, setAdminDelBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/transactions/${id}`);
      setTx(data);
      const metas = await Promise.all(
        (data.attachment_ids || []).map((aid) =>
          api.get(`/attachments/${aid}`).then((r) => r.data).catch(() => null),
        ),
      );
      setAttachments(metas.filter(Boolean));
      // Try to load audit for this entity (admin / auditor only)
      try {
        const a = await api.get(`/audit-logs?entity_id=${id}&page_size=100`);
        setAuditEntries(a.data.items || []);
      } catch {
        setAuditEntries([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 404 || e?.response?.status === 403) navigate("/transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !tx) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
      </div>
    );
  }

  const role = user?.role;
  const isOwner = tx.created_by === user?.id;
  const isAdmin = role === ROLES.SYSTEM_ADMIN || user?.full_access === true;
  const isSalihiAcct = isAdmin || [ROLES.SALIHI_ACCOUNTANT, ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER].includes(role);
  const isSenior = isAdmin || [ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER].includes(role);
  const canReplyOwner = isOwner && role === ROLES.SOMTURK_DATA_ENTRY;
  const editable = isOwner && (tx.status === "draft" || tx.status === "missing_document");

  const doAction = async (action, reason) => {
    setPosting(true);
    try {
      await api.post(`/transactions/${id}/review-action`, { action, reason });
      toast.success(`Marked ${STATUS_LABELS[action] || action}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
      setShowRejectDialog(false);
      setRejectReason("");
    }
  };

  const doPost = async (action, extras = {}) => {
    setPosting(true);
    try {
      await api.post(`/transactions/${id}/post`, { action, ...extras });
      toast.success(action === "process" ? "Processed" : "Posted");
      setPostOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  };

  const openPostModal = async () => {
    // Phase 14 (2026-06-28) — bank-only transaction types do NOT need the
    // expense/pay-from override modal. They already carry their own ledger
    // logic in accounting.py (beginning_bank_balance → 3100 Opening
    // Balance Equity; bank_deposit / bank_withdrawal / bank_transfer →
    // bank-to-cash/bank-to-bank). Just call post directly.
    const NO_MODAL = new Set([
      "beginning_bank_balance",
      "bank_deposit",
      "bank_withdrawal",
      "bank_transfer",
      "income",
    ]);
    if (NO_MODAL.has(tx?.transaction_type)) {
      await doPost("post");
      return;
    }
    // Pre-default bank account based on payment method
    setPostBank(tx?.payment_method === "cash" ? "1000" : "1010");
    setPostExpense("5900");
    try {
      if (!accounts.length) {
        const { data } = await api.get("/accounts");
        setAccounts(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("accounts/load", err?.message || err);
      // Modal still works with default GL codes
    }
    setPostOpen(true);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/transactions/${id}/clarifications`, { message: reply.trim() });
      setReply("");
      toast.success("Message sent.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Phase 9c Fix #8 — admin can delete a single thread message
  const deleteClarification = async (msg_id) => {
    if (!window.confirm("Delete this clarification message? This is logged in the audit trail.")) return;
    try {
      await api.delete(`/transactions/${id}/clarifications/${msg_id}`);
      toast.success("Message deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Phase 9c Fix #8 — admin override delete on the whole transaction
  const adminDeleteTx = async () => {
    setAdminDelBusy(true);
    try {
      const { data } = await api.delete(`/transactions/${id}`);
      toast.success(data.soft
        ? "Soft-deleted (reversing JE posted)."
        : "Transaction permanently removed.");
      navigate("/transactions");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdminDelBusy(false);
      setAdminDelOpen(false);
    }
  };

  const onAddAttachments = async () => {
    if (pendingAttach.length === 0) {
      setShowAddAttach(false);
      return;
    }
    try {
      await api.post(`/transactions/${id}/attachments`, {
        attachment_ids: pendingAttach.map((a) => a.id),
      });
      toast.success("Attachments added.");
      setPendingAttach([]);
      setShowAddAttach(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="tx-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
            <Link to="/transactions"><ArrowLeft className="h-4 w-4 mr-1" /> All transactions</Link>
          </Button>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-slate-900" data-testid="tx-amount">
              {fmtUSD(tx.amount)}
            </h2>
            <Badge variant="outline" className={STATUS_STYLE[tx.status]} data-testid="tx-status">
              {STATUS_LABELS[tx.status] || tx.status}
            </Badge>
            {tx.already_paid_before_system_start && (
              <Badge
                className="bg-amber-100 text-amber-800 border-amber-200"
                data-testid="historical-paid-badge"
                title="Pre-system-start historical record. Bank/cash NOT deducted."
              >
                Historical Paid
              </Badge>
            )}
            {tx.ai_created && (
              <Badge
                className="bg-violet-100 text-violet-700 border-violet-200"
                data-testid="ai-created-badge"
                title="Created via AI Transaction Assistant"
              >
                Created via AI Assistant
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {typeLabel(tx.transaction_type)} · {tx.counterparty_name || "no counterparty"} · {fmt(tx.transaction_date)?.split(",")[0]}
          </p>
        </div>
        {editable && (
          <Button asChild variant="outline" data-testid="edit-draft-button">
            <Link to={`/transactions/${id}/edit`}><Pencil className="h-4 w-4 mr-2" /> Edit draft</Link>
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <Tabs defaultValue="details">
            <TabsList data-testid="tabs">
              <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
              <TabsTrigger value="attachments" data-testid="tab-attachments">
                Attachments ({attachments.length})
              </TabsTrigger>
              <TabsTrigger value="clarifications" data-testid="tab-clarifications">
                Clarification ({(tx.clarification_thread || []).length})
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
            </TabsList>

            <TabsContent value="details">
              <Card><CardContent className="p-5 space-y-3 text-sm">
                <KV label="Date" value={fmt(tx.transaction_date)?.split(",")[0]} origin={tx.field_origins?.transaction_date} />
                <KV label="Type" value={typeLabel(tx.transaction_type)} origin={tx.field_origins?.transaction_type} />
                <KV label="Counterparty" value={tx.counterparty_name || "—"} origin={tx.field_origins?.counterparty_name} />
                <KV label="Reference #" value={tx.reference_number || "—"} origin={tx.field_origins?.reference_number} />
                <KV label="Amount" value={fmtUSD(tx.amount)} origin={tx.field_origins?.amount} />
                <KV label="Currency" value={tx.currency} origin={tx.field_origins?.currency} />
                <KV label="Payment method" value={methodLabel(tx.payment_method)} origin={tx.field_origins?.payment_method} />
                <KV label="Description" value={tx.description || "—"} origin={tx.field_origins?.description} />
                {tx.note ? <KV label="Note" value={tx.note} /> : null}
                {tx.rejection_reason ? (
                  <KV label="Rejection reason" value={tx.rejection_reason} />
                ) : null}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t mt-3 text-xs text-slate-500">
                  <div>Created: <span className="text-slate-700">{fmt(tx.created_at)}</span></div>
                  <div>By: <span className="text-slate-700">{tx.created_by_email}</span></div>
                  <div>Submitted: <span className="text-slate-700">{fmt(tx.submitted_at)}</span></div>
                  <div>Reviewed: <span className="text-slate-700">{fmt(tx.reviewed_at)}</span></div>
                  <div>Posted: <span className="text-slate-700">{fmt(tx.posted_at)}</span></div>
                  <div>Updated: <span className="text-slate-700">{fmt(tx.updated_at)}</span></div>
                </div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="attachments">
              <Card><CardContent className="p-5 space-y-3">
                {tx.status === "missing_document" && isOwner && (
                  <Dialog open={showAddAttach} onOpenChange={setShowAddAttach}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-attachment-button" className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white">
                        <FileText className="h-4 w-4 mr-2" /> Add missing document
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Upload additional documents</DialogTitle>
                        <DialogDescription>These will be attached to this transaction.</DialogDescription>
                      </DialogHeader>
                      <AttachmentDropzone
                        attachments={pendingAttach} onChange={setPendingAttach}
                        testid="add-attach-dropzone"
                      />
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddAttach(false)}>Cancel</Button>
                        <Button onClick={onAddAttachments} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white">
                          Attach
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {attachments.length === 0 ? (
                  <p className="text-sm text-slate-500">No attachments.</p>
                ) : (
                  <ul className="space-y-3" data-testid="attachments-list">
                    {attachments.map((a) => (
                      <li key={a.id} className="border rounded-md p-3" data-testid={`attachment-${a.id}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{a.original_name}</p>
                            <p className="text-xs text-slate-500">{a.mime_type} · {(a.size_bytes / 1024).toFixed(1)} KB</p>
                          </div>
                          <Button
                            onClick={() => downloadAttachment(a.id, a.original_name)}
                            variant="outline" size="sm"
                            data-testid={`download-attachment-${a.id}`}
                          >Download</Button>
                        </div>
                        {a.mime_type?.startsWith("image/") ? (
                          <InlineAttachmentImage att={a} />
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="clarifications">
              <Card><CardContent className="p-5 space-y-4">
                <div className="space-y-3" data-testid="clarification-thread">
                  {(tx.clarification_thread || []).length === 0 ? (
                    <p className="text-sm text-slate-500 italic">
                      No messages yet. Salihi can start the conversation by requesting clarification.
                    </p>
                  ) : (
                    tx.clarification_thread.map((m) => {
                      const isSal = ["salihi_accountant", "salihi_senior_accountant", "salihi_finance_manager", "system_admin"].includes(m.author_role);
                      return (
                        <div
                          key={m.id}
                          className={`flex ${isSal ? "justify-start" : "justify-end"}`}
                          data-testid={`message-${m.id}`}
                        >
                          <div className={`max-w-[80%] rounded-lg p-3 ${isSal ? "bg-sky-50 border border-sky-200" : "bg-slate-100 border border-slate-200"}`}>
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-medium text-slate-600">
                                  {m.author_email} · {m.author_role.replace(/_/g, " ")}
                                </p>
                                <p className="text-sm mt-1 whitespace-pre-wrap break-words">{m.message}</p>
                                <p className="text-[10px] text-slate-400 mt-1">{fmt(m.created_at)}</p>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={() => deleteClarification(m.id)}
                                  className="p-1 rounded hover:bg-rose-100 text-rose-600 shrink-0"
                                  title="Delete message (admin override)"
                                  data-testid={`admin-del-msg-${m.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {(isSalihiAcct || canReplyOwner) && (
                  <div className="pt-3 border-t" data-testid="reply-box">
                    <Label htmlFor="reply" className="text-sm">Your message</Label>
                    <Textarea
                      id="reply" rows={3} value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder={isSalihiAcct ? "Ask SOM-TURK for clarification…" : "Reply to Salihi…"}
                      data-testid="reply-input"
                      className="mt-1"
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        onClick={sendReply} disabled={!reply.trim()}
                        className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                        data-testid="reply-send-button"
                      >
                        <Send className="h-4 w-4 mr-2" /> Send
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="audit">
              <Card><CardContent className="p-5">
                {auditEntries.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">
                    Audit trail is visible to admin and auditor roles.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="audit-trail">
                    {auditEntries.map((a) => (
                      <li key={a.id} className="text-sm border-l-2 border-[#4FA3D1] pl-3 py-1">
                        <p className="font-medium">{a.action}</p>
                        <p className="text-xs text-slate-500">
                          {a.user_email} · {fmt(a.timestamp)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right action panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Actions</CardTitle>
              <CardDescription>Available to your role.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {isSalihiAcct && (
                <>
                  <ActionBtn label="Mark Under Review" icon={ClipboardCheck} testid="action-under_review"
                    onClick={() => doAction("under_review")} disabled={posting || ["posted", "reported", "draft"].includes(tx.status)} />
                  <ActionBtn label="Request Clarification" icon={MessageSquare} testid="action-clarification_needed"
                    onClick={() => doAction("clarification_needed")} disabled={posting || ["posted", "reported", "draft"].includes(tx.status)} />
                  <ActionBtn label="Mark Missing Document" icon={FileWarning} testid="action-missing_document"
                    onClick={() => doAction("missing_document")} disabled={posting || ["posted", "reported", "draft"].includes(tx.status)} />
                  <ActionBtn label="Verify" icon={ShieldCheck} testid="action-verified" color="emerald"
                    onClick={() => doAction("verified")} disabled={posting || ["posted", "reported", "draft", "verified"].includes(tx.status)} />
                  <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full justify-start border-red-200 text-red-700 hover:bg-red-50" data-testid="action-rejected"
                        disabled={posting || ["posted", "reported", "draft"].includes(tx.status)}>
                        <XCircle className="h-4 w-4 mr-2" /> Reject
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Reject transaction</DialogTitle>
                        <DialogDescription>Provide a reason — this will be visible to the submitter.</DialogDescription>
                      </DialogHeader>
                      <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
                        <Button onClick={() => doAction("rejected", rejectReason)}
                          className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject">
                          Reject
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}
              {isSenior && (
                <>
                  <ActionBtn label="Process" icon={PlayCircle} testid="action-process" color="teal"
                    onClick={() => doPost("process")} disabled={posting || tx.status !== "verified"} />
                  <ActionBtn label="Post" icon={CheckCircle2} testid="action-post" color="violet"
                    onClick={openPostModal} disabled={posting || tx.status !== "processed"} />
                </>
              )}
              {!isSalihiAcct && !isSenior && (
                <p className="text-sm text-slate-500">You can view this transaction.</p>
              )}
            </CardContent>
          </Card>

          {/* Phase 9c Fix #8 — Admin Override panel */}
          {isAdmin && (
            <Card className="border-2 border-amber-200 bg-amber-50/40" data-testid="admin-override-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <ShieldAlert className="h-4 w-4" /> Admin Override
                </CardTitle>
                <CardDescription className="text-xs text-amber-700/80">
                  System Administrator can edit or delete this transaction regardless of status. Every action is audit-logged. Posted JEs are reversed and re-posted automatically to keep the books balanced.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline"
                        className="w-full justify-start border-amber-200 text-amber-700 hover:bg-amber-50"
                        onClick={() => navigate(`/transactions/${id}/edit?override=admin`)}
                        data-testid="admin-edit-override-button">
                  <Pencil className="h-4 w-4 mr-2" /> Edit (override)
                </Button>
                <Button variant="outline"
                        className="w-full justify-start border-rose-200 text-rose-700 hover:bg-rose-50"
                        onClick={() => setAdminDelOpen(true)}
                        data-testid="admin-delete-override-button">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete (override)
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Admin delete confirm dialog */}
          <Dialog open={adminDelOpen} onOpenChange={setAdminDelOpen}>
            <DialogContent data-testid="admin-delete-detail-dialog">
              <DialogHeader>
                <DialogTitle className="text-rose-700">Delete transaction (admin override)</DialogTitle>
                <DialogDescription>
                  {["processed", "posted", "reported"].includes(tx?.status) ? (
                    <span>
                      Deleting a <strong>{STATUS_LABELS[tx.status] || tx.status}</strong> transaction will
                      create a reversing journal entry to keep the books balanced. The original record
                      will be marked <code>deleted_by_admin</code> and preserved for audit. Continue?
                    </span>
                  ) : (
                    <span>
                      This record is pre-posting; it will be <strong>permanently removed</strong> from the
                      database. Continue?
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAdminDelOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={adminDeleteTx} disabled={adminDelBusy}
                        data-testid="confirm-admin-delete-detail">
                  {adminDelBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Post modal — Senior+ picks GL accounts */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent data-testid="post-modal">
          <DialogHeader>
            <DialogTitle>Post to General Ledger</DialogTitle>
            <DialogDescription>
              Pick the debit account (Dr) and the pay-from account (Cr). The chosen
              accounts are recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          {/* Phase 50 (2026-08-04) - guidance callout: liability accounts are
              now selectable on the Dr side, but PAYE accruals must originate
              from the Payroll Module, not this modal. */}
          <div
            className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="post-modal-payroll-warning"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>For salaries and PAYE accruals, use the Payroll Module.</strong>{" "}
              Select Payroll Tax Payable here only when settling an existing PAYE liability.
            </span>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Debit account</Label>
              <Select value={postExpense} onValueChange={setPostExpense}>
                <SelectTrigger
                  data-testid="post-expense-select"
                  data-testid-alias="post-debit-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accounts
                    // Phase 50 (2026-08-04) - Dr dropdown now allows both
                    // expense AND liability accounts (e.g. 2100 Payroll Tax
                    // Payable, 2110 Tax Payable) so accountants can settle
                    // an existing liability directly. Assets, equity, and
                    // revenue remain excluded as a deliberate guardrail.
                    .filter((a) => a.type === "expense" || a.type === "liability")
                    .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
                    .map((a) => (
                      <SelectItem key={a.id} value={a.code} data-testid={`post-debit-option-${a.code}`}>
                        {a.code} — {a.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pay-from account</Label>
              <Select value={postBank} onValueChange={setPostBank}>
                <SelectTrigger data-testid="post-bank-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {accounts
                    .filter((a) => {
                      // Phase 50-B (2026-08-05) - strict bank-or-cash
                      // whitelist mirroring the backend predicate
                      // `_is_bank_or_cash_account` in accounting.py so a
                      // Senior cannot pick a non-cash asset (1200 AR,
                      // 1500 Office Equipment, etc.). Rule: asset AND
                      // (code == 1000 OR code == 1100 OR code in
                      // 1010..1099 OR parent_code == "1010"). Inactive
                      // accounts are excluded.
                      if (!a || a.type !== "asset" || a.is_active === false) return false;
                      const code = String(a.code || "").trim();
                      if (!code) return false;
                      if (code === "1000" || code === "1100") return true;
                      if (a.parent_code === "1010") return true;
                      const n = Number(code);
                      return Number.isInteger(n) && n >= 1010 && n <= 1099;
                    })
                    .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
                    .map((a) => (
                    <SelectItem key={a.id} value={a.code} data-testid={`post-bank-option-${a.code}`}>{a.code} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accounts.filter((a) => {
                if (!a || a.type !== "asset" || a.is_active === false) return false;
                const code = String(a.code || "").trim();
                if (!code) return false;
                if (code === "1000" || code === "1100") return true;
                if (a.parent_code === "1010") return true;
                const n = Number(code);
                return Number.isInteger(n) && n >= 1010 && n <= 1099;
              }).length === 0 && (
                <p className="mt-1 text-xs text-amber-700" data-testid="post-modal-no-banks-msg">
                  No bank or cash accounts available. Please add one in Chart of Accounts.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostOpen(false)}>Cancel</Button>
            <Button
              onClick={() => doPost("post", { posting_expense_account: postExpense, posting_bank_account: postBank })}
              disabled={posting || !postBank}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="confirm-post-btn"
            >
              Post to GL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RecordTrail entity_type="transaction" entity_id={tx?.id} record={tx || {}} />
    </div>
  );
}

function KV({ label, value, origin }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-baseline">
      <div className="text-xs uppercase tracking-wide text-slate-500 col-span-1 flex items-center gap-2">
        {label}
      </div>
      <div className="col-span-2 flex items-center gap-2 flex-wrap">
        <span className="text-slate-900 font-medium break-words">{value}</span>
        {origin ? <OriginBadge origin={origin} /> : null}
      </div>
    </div>
  );
}

function ActionBtn({ label, icon: Icon, onClick, disabled, testid, color }) {
  const colors = {
    emerald: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    teal: "border-teal-200 text-teal-700 hover:bg-teal-50",
    violet: "border-violet-200 text-violet-700 hover:bg-violet-50",
  };
  return (
    <Button
      variant="outline"
      className={`w-full justify-start ${colors[color] || ""}`}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
    >
      <Icon className="h-4 w-4 mr-2" /> {label}
    </Button>
  );
}
